/**
 * Timezone helpers for MotionIQ.
 *
 * Keep the surface small and dependency-free. We do not pull in a date
 * library — `Intl` is sufficient for everything we need.
 */

const FALLBACK_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Europe/Stockholm",
  "Europe/Istanbul",
  "Europe/Moscow",
  "Africa/Cairo",
  "Africa/Lagos",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Perth",
  "Australia/Sydney",
  "Pacific/Auckland",
];

/** Detect the browser's IANA timezone. Falls back to UTC if the API is missing. */
export function getBrowserTz() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || "UTC";
  } catch (_e) {
    return "UTC";
  }
}

/** Full list of IANA timezones supported by the runtime, with a curated
 *  fallback when `Intl.supportedValuesOf` is unavailable (older Safari etc). */
export function getSupportedTimezones() {
  try {
    if (typeof Intl !== "undefined" && typeof Intl.supportedValuesOf === "function") {
      const list = Intl.supportedValuesOf("timeZone");
      if (Array.isArray(list) && list.length > 0) return list;
    }
  } catch (_e) {
    /* ignore — fall through */
  }
  return FALLBACK_TIMEZONES;
}

/** "today" in a given IANA timezone, formatted as YYYY-MM-DD. */
export function todayInTz(tz) {
  return dateInTz(new Date(), tz);
}

/** Format any Date as YYYY-MM-DD in the given timezone. */
export function dateInTz(date, tz) {
  try {
    // en-CA produces YYYY-MM-DD which we can map directly. Using formatToParts
    // is more reliable than parsing locale-specific output.
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const y = parts.find((p) => p.type === "year")?.value || "1970";
    const m = parts.find((p) => p.type === "month")?.value || "01";
    const d = parts.find((p) => p.type === "day")?.value || "01";
    return `${y}-${m}-${d}`;
  } catch (_e) {
    return date.toISOString().slice(0, 10);
  }
}

/** Parts of a Date in a given timezone — useful for tz-aware "today" math
 *  without dragging in a date library. Returns numbers, never strings. */
export function partsInTz(date, tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);
    const get = (t) => parseInt(parts.find((p) => p.type === t)?.value || "0", 10);
    return {
      year: get("year"),
      month: get("month"), // 1-12
      day: get("day"),
      hour: get("hour"),
      minute: get("minute"),
    };
  } catch (_e) {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hour: date.getUTCHours(),
      minute: date.getUTCMinutes(),
    };
  }
}

/** Whether `Intl.DateTimeFormat` accepts a given zone. Used to validate the
 *  saved-setting before we hand it to other helpers. */
export function isValidTz(tz) {
  if (!tz || typeof tz !== "string") return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch (_e) {
    return false;
  }
}

/** Resolve the timezone the app should use right now, in this order:
 *   1. The user's saved Settings.timezone (if valid)
 *   2. The browser-detected zone
 *   3. UTC
 */
export function resolveTz(settings) {
  const saved = settings?.timezone;
  if (isValidTz(saved)) return saved;
  return getBrowserTz();
}
